/**
 * SmsAutomationEditorPage.tsx
 *
 * Dedicated SMS automation editor at /marketing/automations/sms/:id.
 * Split layout: left panel (automation details + contacts), right panel (step flow).
 * Step editor modal with SmsEditor + SmsPhoneMockup, SMS template picker,
 * timing modal, activation/pause controls.
 * Matches the PHP sms-automation-details.php behavior exactly.
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Save, Trash2, MoreVertical, MessageCircle, Plus, Pencil, X, FileText, Paperclip, GripVertical } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Select } from '@/components/ui/Select';
import { FormField } from '@/components/ui/FormField';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { PauseAutomationDialog } from '@/components/marketing/PauseAutomationDialog';
import { Spinner } from '@/components/ui/Spinner';
import { AutomationStepMenu } from '@/components/marketing/AutomationStepMenu';
import { useToast } from '@/hooks/useToast';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import { useStepDragReorder } from '@/hooks/useStepDragReorder';
import { cn } from '@/lib/utils';
import type { BadgeVariant } from '@/types/common.types';
import * as marketingSvc from '@/services/marketing.service';
import type { AutomationStep, SmsMarketingTemplate } from '@/services/marketing.service';
import { SmsEditor, SmsPhoneMockup } from '@/components/sms-editor';
import SmsTemplatePickerModal from '@/components/sms-editor/SmsTemplatePickerModal';
import {
  getSmsSegments, ensureSmsOptOut, hasSmsMessageContent, hasSmsOptOut, SMS_OPT_OUT_TOKEN,
} from '@/components/sms-editor/sms-editor-constants';

// ─── Constants ───────────────────────────────────────────────────────────────

const STATUS_BADGE: Record<string, BadgeVariant> = {
  draft: 'neutral', active: 'success', paused: 'warning',
};

const EXIT_CRITERIA_OPTIONS = [
  { value: '0', label: 'Contacts have received all messages' },
  { value: '1', label: 'Contacts are removed from the list' },
];

const DELAY_UNIT_OPTIONS = [
  { value: 'minutes', label: 'Minutes' },
  { value: 'hours', label: 'Hours' },
  { value: 'days', label: 'Days' },
];

interface EntryList {
  emailListId: number;
  name: string;
  listType: string;
  patientCount: number;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getDelayParts(minutes: number): { value: number; unit: string } {
  if (minutes === 0) return { value: 0, unit: 'days' };
  if (minutes < 60) return { value: minutes, unit: 'minutes' };
  if (minutes < 1440) return { value: Math.floor(minutes / 60), unit: 'hours' };
  return { value: Math.floor(minutes / 1440), unit: 'days' };
}

function delayToMinutes(value: number, unit: string): number {
  if (unit === 'hours') return value * 60;
  if (unit === 'days') return value * 1440;
  return value;
}

function getOrdinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

function formatDelay(minutes: number, index: number): string {
  const ord = getOrdinal(index + 1);
  if (minutes === 0) return `Send the ${ord} SMS instantly`;
  if (minutes < 60) return `Wait ${minutes} ${minutes === 1 ? 'minute' : 'minutes'} then send ${ord} SMS`;
  if (minutes < 1440) {
    const h = Math.floor(minutes / 60);
    return `Wait ${h} ${h === 1 ? 'hour' : 'hours'} then send ${ord} SMS`;
  }
  const d = Math.floor(minutes / 1440);
  return `Wait ${d} ${d === 1 ? 'day' : 'days'} then send ${ord} SMS`;
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function getSegmentColor(segments: number): string {
  if (segments <= 4) return 'text-green-600';
  if (segments <= 8) return 'text-blue-600';
  return 'text-red-600';
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function SmsAutomationEditorPage() {
  const { id: idParam } = useParams<{ id: string }>();
  const automationId = Number(idParam);
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();

  // ── State ──
  const [automationName, setAutomationName] = useState('');
  const [automationDescription, setAutomationDescription] = useState('');
  const [selectedEntryListId, setSelectedEntryListId] = useState('');
  const [exitCriteriaVal, setExitCriteriaVal] = useState('0');
  const [allowReentry, setAllowReentry] = useState(false);
  const [formInitialized, setFormInitialized] = useState(false);
  const baselineRef = useRef<Record<string, unknown>>({});

  const [stepEditorUnsaved, setStepEditorUnsaved] = useState(false);
  const [suppressUnsaved, setSuppressUnsaved] = useState(true);
  const [showStepEditor, setShowStepEditor] = useState(false);
  const [editingStepIndex, setEditingStepIndex] = useState(-1);
  const [editorMessageBody, setEditorMessageBody] = useState('');
  const [editorMediaUrls, setEditorMediaUrls] = useState<string[]>([]);
  const [showTimingModal, setShowTimingModal] = useState(false);
  const [timingStepIndex, setTimingStepIndex] = useState(-1);
  const [timingValue, setTimingValue] = useState(0);
  const [timingUnit, setTimingUnit] = useState('days');
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [loadingOverlay, setLoadingOverlay] = useState('');
  const [pauseConfirmOpen, setPauseConfirmOpen] = useState(false);
  const [listTypeFilter, setListTypeFilter] = useState<'static' | 'dynamic'>('static');
  const [localSteps, setLocalSteps] = useState<AutomationStep[]>([]);

  // ── Queries ──
  const { data: automation, isLoading } = useQuery({
    queryKey: ['sms-automation-editor', automationId],
    queryFn: () => marketingSvc.getSmsAutomation(automationId),
    enabled: automationId > 0,
  });

  const { data: entryLists = [] } = useQuery({
    queryKey: ['marketing-entry-lists', 'sms'],
    queryFn: () => marketingSvc.listEntryLists('sms'),
  });

  // Sync local steps
  useEffect(() => {
    if (automation?.steps) setLocalSteps(automation.steps);
  }, [automation?.steps]);

  // Initialize form fields from automation data
  useEffect(() => {
    if (automation && !formInitialized) {
      const resolvedName = automation.name ?? '';
      const resolvedDesc = automation.description ?? '';
      const resolvedEntryList = automation.entryListId?.toString() ?? '';
      const resolvedExit = automation.exitCriteria?.toString() ?? '0';
      const resolvedReentry = !!automation.allowReentry;

      setAutomationName(resolvedName);
      setAutomationDescription(resolvedDesc);
      if (!selectedEntryListId) setSelectedEntryListId(resolvedEntryList);
      setExitCriteriaVal(resolvedExit);
      setAllowReentry(resolvedReentry);

      baselineRef.current = {
        name: resolvedName, description: resolvedDesc, entryListId: resolvedEntryList,
        exitCriteria: resolvedExit, allowReentry: resolvedReentry,
      };
      setFormInitialized(true);
    }
  }, [automation, formInitialized, selectedEntryListId]);

  const hasUnsavedChanges = useMemo(() => {
    if (!automation || !formInitialized) return false;
    const b = baselineRef.current;
    if (automationName !== b.name) return true;
    if (automationDescription !== b.description) return true;
    if (selectedEntryListId !== b.entryListId) return true;
    if (exitCriteriaVal !== b.exitCriteria) return true;
    if (allowReentry !== b.allowReentry) return true;
    return false;
  }, [automation, formInitialized, automationName, automationDescription, selectedEntryListId, exitCriteriaVal, allowReentry]);

  useEffect(() => {
    if (automation && automation.status === 'active') {
      navigate(`/marketing/automations/sms/${automationId}/stats`, { replace: true });
    }
  }, [automation, automationId, navigate]);

  // Lift suppression
  useEffect(() => {
    const timer = setTimeout(() => setSuppressUnsaved(false), 2000);
    return () => clearTimeout(timer);
  }, []);

  // Navigation guards
  useUnsavedChangesGuard(hasUnsavedChanges || stepEditorUnsaved);

  // ── Mutations ──
  const saveMut = useMutation({
    mutationFn: (data: Parameters<typeof marketingSvc.updateSmsAutomation>[1]) =>
      marketingSvc.updateSmsAutomation(automationId, data),
    onSuccess: () => {
      baselineRef.current = {
        name: automationName, description: automationDescription,
        entryListId: selectedEntryListId, exitCriteria: exitCriteriaVal,
        allowReentry,
      };
      qc.invalidateQueries({ queryKey: ['sms-automation-editor', automationId] });
      toast.success('Automation saved');
    },
    onError: () => toast.error('Failed to save'),
  });

  const addStepMut = useMutation({
    mutationFn: (data: Partial<AutomationStep>) => marketingSvc.addSmsAutomationStep(automationId, data),
    onSuccess: (newStep) => {
      setLocalSteps(prev => [...prev.filter(s => !(s as any).loading), newStep]);
      qc.invalidateQueries({ queryKey: ['sms-automation-editor', automationId] });
      toast.success('Step added');
    },
    onError: () => {
      setLocalSteps(prev => prev.filter(s => !(s as any).loading));
      toast.error('Failed to add step');
    },
  });

  const updateStepMut = useMutation({
    mutationFn: ({ stepId, data }: { stepId: number; data: Partial<AutomationStep> }) =>
      marketingSvc.updateSmsAutomationStep(automationId, stepId, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sms-automation-editor', automationId] }),
  });

  const deleteStepMut = useMutation({
    mutationFn: (stepId: number) => marketingSvc.deleteSmsAutomationStep(automationId, stepId),
    onSuccess: (_, stepId) => {
      setLocalSteps(prev => prev.filter(s => s.stepId !== stepId));
      qc.invalidateQueries({ queryKey: ['sms-automation-editor', automationId] });
      toast.success('Step deleted');
    },
    onError: () => toast.error('Failed to delete step'),
  });

  // ── Step reordering ──
  // Mirrors EmailAutomationEditorPage: optimistic move, hard revert on failure.
  const reorderRevertRef = useRef<AutomationStep[] | null>(null);

  const reorderMut = useMutation({
    mutationFn: (stepIds: number[]) => marketingSvc.reorderSmsAutomationSteps(automationId, stepIds),
    onSuccess: ({ contactsRemapped }) => {
      reorderRevertRef.current = null;
      qc.invalidateQueries({ queryKey: ['sms-automation-editor', automationId] });
      toast.success(
        contactsRemapped > 0
          ? `Steps reordered — ${contactsRemapped.toLocaleString()} in-progress contact${contactsRemapped === 1 ? '' : 's'} realigned`
          : 'Steps reordered',
      );
    },
    onError: (err: any) => {
      if (reorderRevertRef.current) setLocalSteps(reorderRevertRef.current);
      reorderRevertRef.current = null;
      toast.error(err?.response?.data?.error?.message || 'Failed to reorder steps');
    },
  });

  const applyReorder = useCallback((next: AutomationStep[], previous: AutomationStep[]) => {
    reorderRevertRef.current = previous;
    setLocalSteps(next);
    reorderMut.mutate(next.map(s => s.stepId));
  }, [reorderMut]);

  const stepDrag = useStepDragReorder<AutomationStep>({
    items: localSteps,
    onReorder: applyReorder,
    disabled: localSteps.some(s => (s as any).loading) || reorderMut.isPending,
  });

  const moveStep = useCallback((index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= localSteps.length) return;
    const next = [...localSteps];
    [next[index], next[target]] = [next[target], next[index]];
    applyReorder(next, localSteps);
  }, [localSteps, applyReorder]);

  const activateMut = useMutation({
    mutationFn: () => marketingSvc.activateSmsAutomation(automationId),
    onSuccess: () => {
      setLoadingOverlay('');
      qc.invalidateQueries({ queryKey: ['sms-automation-editor', automationId] });
      toast.success('Automation activated');
      navigate(`/marketing/automations/sms/${automationId}/stats`);
    },
    onError: (err: any) => {
      setLoadingOverlay('');
      const serverMsg = err?.response?.data?.message || err?.response?.data?.error?.message || err?.message || 'Failed to activate';
      const debugInfo = err?.response?.data?.file ? ` (${err.response.data.file}:${err.response.data.line})` : '';
      toast.error(serverMsg + debugInfo);
      console.error('Activate error:', err?.response?.data || err);
    },
  });

  const pauseMut = useMutation({
    mutationFn: () => marketingSvc.pauseSmsAutomation(automationId),
    onSuccess: () => {
      setLoadingOverlay('');
      qc.invalidateQueries({ queryKey: ['sms-automation-editor', automationId] });
      toast.success('Automation paused');
    },
    onError: () => { setLoadingOverlay(''); toast.error('Failed to pause'); },
  });

  const deleteMut = useMutation({
    mutationFn: () => marketingSvc.deleteSmsAutomation(automationId),
    onSuccess: () => { navigate('/marketing/automations'); toast.success('Automation deleted'); },
    onError: () => toast.error('Cannot delete automation with active enrollments'),
  });

  // ── Computed ──
  const isDraft = automation?.status === 'draft';
  const isActive = automation?.status === 'active';
  const isPaused = automation?.status === 'paused';
  const contacts = automation?.contactCounts ?? { active: 0, completed: 0, removed: 0, unsubscribed: 0, total: 0 };

  const filteredLists = useMemo(() =>
    (entryLists as EntryList[]).filter(l => (l.listType || 'static') === listTypeFilter),
    [entryLists, listTypeFilter]
  );

  const activationMissing = useMemo(() => {
    if (!automation) return ['Loading...'];
    const missing: string[] = [];
    if (hasUnsavedChanges || stepEditorUnsaved) missing.push('Save all changes first');
    if (!automation.name?.trim()) missing.push('Automation name');
    if (!automation.entryListId) missing.push('Entry list');
    let hasValid = false;
    let hasIncomplete = false;
    let missingOptOut = false;
    for (const step of localSteps) {
      if ((step as any).loading) continue;
      if (hasSmsMessageContent(step.messageBody)) {
        hasValid = true;
        // Saving a step adds the token, so this only bites on a step written
        // elsewhere; the API refuses the activation too.
        if (!hasSmsOptOut(step.messageBody)) missingOptOut = true;
      } else {
        hasIncomplete = true;
      }
    }
    if (!hasValid) missing.push('At least one step with message content');
    if (hasIncomplete) missing.push('All steps must have message content');
    if (missingOptOut) missing.push(`Opt-out instructions (${SMS_OPT_OUT_TOKEN}) in every message`);
    return missing;
  }, [automation, localSteps, hasUnsavedChanges, stepEditorUnsaved]);

  const canActivate = activationMissing.length === 0;

  // ── Handlers ──
  function handleSave() {
    if (!automation) return;
    setLoadingOverlay('Saving...');
    saveMut.mutate({
      name: automationName || automation.name,
      description: automationDescription || undefined,
      entryListId: isDraft ? (Number(selectedEntryListId) || automation.entryListId) : automation.entryListId,
      exitCriteria: Number(exitCriteriaVal) || automation.exitCriteria,
      allowReentry,
    }, { onSettled: () => setLoadingOverlay('') });
  }

  const handleBack = useCallback(() => {
    navigate('/marketing/automations');
  }, [navigate]);

  function handleActivate() {
    if (hasUnsavedChanges || stepEditorUnsaved) {
      toast.error('Please save all changes before activating.');
      return;
    }
    if (!canActivate) {
      toast.error('Missing: ' + activationMissing.join(', '));
      return;
    }
    setLoadingOverlay(isPaused ? 'Resuming...' : 'Activating...');
    activateMut.mutate();
  }

  // The confirm dialog owns the button spinner; errors surface via pauseMut.onError.
  async function confirmPause() {
    try {
      await pauseMut.mutateAsync();
    } catch {
      /* surfaced by pauseMut.onError */
    }
  }

  function handleAddStep() {
    const tempStep: AutomationStep & { loading?: boolean } = {
      stepId: -Date.now(),
      stepOrder: localSteps.length + 1,
      delayMinutes: localSteps.length > 0 ? 1440 : 0,
      messageBody: '',
      mediaUrls: [],
      loading: true,
    } as any;
    setLocalSteps(prev => [...prev, tempStep]);
    // No stepOrder — the server appends. See the email editor for why.
    addStepMut.mutate({ delayMinutes: localSteps.length > 0 ? 1440 : 0, messageBody: '' });
  }

  function openStepEditor(index: number) {
    const step = localSteps[index];
    if (!step) return;
    setEditingStepIndex(index);
    setEditorMessageBody(step.messageBody || '');
    setEditorMediaUrls(step.mediaUrls?.slice() || []);
    setStepEditorUnsaved(false);
    setShowStepEditor(true);
  }

  function handleSaveStepContent() {
    if (editingStepIndex < 0) return;
    const step = localSteps[editingStepIndex];
    if (!step) return;
    // Every step goes out with opt-out instructions: the token is appended when
    // the message has none, the way the email editors add the unsubscribe
    // footer. The send pipeline applies the same rule as a backstop.
    const body = ensureSmsOptOut(editorMessageBody);
    if (body !== editorMessageBody) {
      setEditorMessageBody(body);
      toast.info('Opt-out instructions added', `${SMS_OPT_OUT_TOKEN} was added to the end of the message.`);
    }
    setLocalSteps(prev => prev.map((s, i) => i === editingStepIndex ? { ...s, messageBody: body, mediaUrls: editorMediaUrls } : s));
    updateStepMut.mutate({ stepId: step.stepId, data: { messageBody: body, mediaUrls: editorMediaUrls } });
    setStepEditorUnsaved(false);
    setShowStepEditor(false);
    toast.success('Content saved');
  }

  function handleCloseStepEditor() {
    if (stepEditorUnsaved && !confirm('You have unsaved changes. Close anyway?')) return;
    setStepEditorUnsaved(false);
    setShowStepEditor(false);
    setEditingStepIndex(-1);
  }

  function handleApplyTemplate(tpl: SmsMarketingTemplate) {
    if (!tpl) return;
    const hasExisting = editorMessageBody.trim().length > 0 || editorMediaUrls.length > 0;
    if (hasExisting && !confirm(`Replace current content with the template "${tpl.name}"?`)) return;
    // A template saved before the opt-out rule may lack the token; add it as
    // the content arrives so the editor never shows a message without it.
    setEditorMessageBody(ensureSmsOptOut(tpl.messageBody || ''));
    setEditorMediaUrls(tpl.mediaUrls || []);
    setShowTemplatePicker(false);
    setStepEditorUnsaved(true);
  }

  function openTimingModal(index: number) {
    const step = localSteps[index];
    if (!step) return;
    setTimingStepIndex(index);
    const dp = getDelayParts(step.delayMinutes);
    setTimingValue(dp.value);
    setTimingUnit(dp.unit);
    setShowTimingModal(true);
  }

  function handleSaveTiming() {
    if (timingStepIndex < 0) return;
    const step = localSteps[timingStepIndex];
    if (!step) return;
    const dm = delayToMinutes(timingValue, timingUnit);
    setLocalSteps(prev => prev.map((s, i) => i === timingStepIndex ? { ...s, delayMinutes: dm } : s));
    updateStepMut.mutate({ stepId: step.stepId, data: { delayMinutes: dm } });
    setShowTimingModal(false);
    toast.success('Timing saved');
  }

  function handleDeleteStep(index: number) {
    const step = localSteps[index];
    if (!step || !confirm('Delete this SMS step?')) return;
    deleteStepMut.mutate(step.stepId);
  }

  // Keyboard handlers
  useEffect(() => {
    function handler(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        if (showStepEditor) handleCloseStepEditor();
        else if (showTemplatePicker) setShowTemplatePicker(false);
      }
    }
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  });

  // ── Loading ──
  if (isLoading || !automation) {
    return <div className="flex items-center justify-center min-h-[60vh]"><Spinner size="lg" /></div>;
  }

  return (
    <div className="flex flex-col h-full">
      {/* Loading Overlay */}
      {loadingOverlay && (
        <div className="fixed inset-0 bg-white/70 z-[99999] flex flex-col items-center justify-center gap-4">
          <Spinner size="lg" />
          <span className="text-sm font-semibold text-slate-600">{loadingOverlay}</span>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-white shrink-0 relative z-40">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={handleBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-lg font-semibold text-slate-900">{automation.name || 'Untitled Automation'}</h1>
          <Badge variant={STATUS_BADGE[automation.status] ?? 'neutral'} dot>{capitalize(automation.status)}</Badge>
        </div>
        <div className="flex items-center gap-2">
          {(hasUnsavedChanges || stepEditorUnsaved) && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 border border-amber-300 rounded text-amber-700 text-xs font-medium">
              <span className="w-2 h-2 bg-amber-400 rounded-full animate-pulse" />
              Unsaved changes
            </div>
          )}
          <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" leftIcon={<Save className="h-4 w-4" />} onClick={handleSave}>
            Save Changes
          </Button>
          {isDraft && (
            <Button size="sm" disabled={!canActivate} title={canActivate ? '' : 'Missing: ' + activationMissing.join(', ')} onClick={handleActivate}>
              Activate
            </Button>
          )}
          {isActive && <Button variant="secondary" size="sm" onClick={() => setPauseConfirmOpen(true)}>Pause</Button>}
          {isPaused && (
            <>
              <Button variant="secondary" size="sm" onClick={() => navigate(`/marketing/automations/sms/${automationId}/stats`)}>View Stats</Button>
              <Button size="sm" onClick={handleActivate}>Resume</Button>
            </>
          )}
          {isDraft && (
            <div className="relative">
              <Button variant="ghost" size="sm" onClick={() => setShowMoreMenu(!showMoreMenu)}>
                <MoreVertical className="h-4 w-4" />
              </Button>
              {showMoreMenu && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowMoreMenu(false)} />
                  <div className="absolute right-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-50 min-w-[160px]">
                    <button className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 rounded-lg" onClick={() => { setShowMoreMenu(false); setShowDeleteConfirm(true); }}>
                      <Trash2 className="inline h-4 w-4 mr-2" />Delete Automation
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Split Panel */}
      <div className="flex flex-1 min-h-0">
        {/* Left Panel */}
        <div className="w-[42%] min-w-[380px] max-w-[520px] border-r border-slate-200 overflow-y-auto">
          <form id="automationForm" className="p-5 space-y-5">
            <Card>
              <CardHeader><span className="font-semibold flex items-center gap-2"><MessageCircle className="h-4 w-4 text-emerald-600" /> SMS Automation Details</span></CardHeader>
              <CardBody className="space-y-4">
                <FormField label="Automation Name" required>
                  <Input name="name" value={automationName} onChange={(e) => setAutomationName(e.target.value)} placeholder="e.g., Welcome Series, Appointment Reminders" />
                </FormField>
                <FormField label="Description" helpText="For internal reference only">
                  <Textarea autoResize name="description" value={automationDescription} onChange={(e) => setAutomationDescription(e.target.value)} placeholder="Internal notes about this automation..." rows={2} />
                </FormField>

                <FormField
                  label="Entry List"
                  helpText="Automation triggers when a contact is added to this list"
                  labelExtra={isDraft ? (
                    <span className="ml-auto flex items-center gap-1">
                      <button type="button" className={cn('px-2 py-1 text-xs font-semibold border rounded', listTypeFilter === 'static' ? 'bg-blue-50 border-blue-600 text-blue-700' : 'bg-slate-50 border-slate-200 text-slate-500')} onClick={() => setListTypeFilter('static')}>Static</button>
                      <button type="button" className={cn('px-2 py-1 text-xs font-semibold border rounded', listTypeFilter === 'dynamic' ? 'bg-orange-50 border-orange-600 text-orange-700' : 'bg-slate-50 border-slate-200 text-slate-500')} onClick={() => setListTypeFilter('dynamic')}>Dynamic</button>
                    </span>
                  ) : undefined}
                >
                  {isDraft ? (
                    <Select
                      name="entry_list_id"
                      value={selectedEntryListId}
                      onChange={v => setSelectedEntryListId(v || '')}
                      options={[
                        { value: '', label: 'Select a list...' },
                        ...filteredLists.map(l => ({ value: l.emailListId.toString(), label: `${l.name} (${capitalize(l.listType || 'static')}) — ${l.patientCount.toLocaleString()} phones` })),
                      ]}
                    />
                  ) : (
                    <Input value={automation.entryListName || 'No list selected'} disabled className="bg-slate-50" />
                  )}
                  {!isDraft && <input type="hidden" name="entry_list_id" value={automation.entryListId || ''} />}
                </FormField>

                <FormField label="Exit Criteria" helpText="When contacts will exit and stop receiving messages">
                  <Select name="exit_criteria" value={exitCriteriaVal} options={EXIT_CRITERIA_OPTIONS} onChange={v => setExitCriteriaVal(v || '0')} />
                </FormField>

                <label className="flex items-center gap-2.5 cursor-pointer">
                  <input type="checkbox" name="allow_reentry" value="1" checked={allowReentry} onChange={(e) => setAllowReentry(e.target.checked)} className="w-4 h-4" />
                  <span className="text-sm font-semibold">Allow Re-entry</span>
                </label>
                <p className="text-xs text-slate-500 ml-7 -mt-2">Contacts who leave and return to the list will restart the automation</p>
              </CardBody>
            </Card>

            {contacts.total > 0 && (
              <Card>
                <CardHeader><span className="font-semibold">Contacts</span></CardHeader>
                <CardBody>
                  <div className="flex gap-5 flex-wrap text-sm">
                    <div><strong>{contacts.active.toLocaleString()}</strong> <span className="text-slate-500">Active</span></div>
                    <div><strong>{contacts.completed.toLocaleString()}</strong> <span className="text-slate-500">Completed</span></div>
                    <div><strong>{contacts.total.toLocaleString()}</strong> <span className="text-slate-500">Total</span></div>
                  </div>
                </CardBody>
              </Card>
            )}
          </form>
        </div>

        {/* Right Panel: Steps */}
        <div className="flex-1 min-w-[400px] overflow-y-auto bg-slate-50">
          <div className="px-6 pt-4 pb-2 text-lg font-bold text-slate-800">SMS Steps</div>
          <div className="px-6 pb-6">
            {localSteps.length === 0 ? (
              <>
                <div className="flex items-center gap-3 p-3 bg-white border border-slate-200 rounded cursor-pointer hover:border-blue-500 transition-colors" onClick={() => handleAddStep()}>
                  <span className="flex-1 text-sm text-slate-700">Send the 1st SMS instantly</span>
                  <Pencil className="h-4 w-4 text-slate-400" />
                </div>
                <div className="w-0.5 h-6 bg-slate-200 ml-5" />
                <div className="bg-slate-50 border border-slate-200 rounded overflow-hidden cursor-pointer hover:border-blue-500 transition-colors" onClick={() => handleAddStep()}>
                  <div className="flex items-stretch">
                    <div className="bg-slate-200 px-1.5 py-3 text-[11px] font-semibold text-slate-500 flex items-center justify-center" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>SMS 1</div>
                    <div className="flex-1 flex items-center justify-center p-8 text-slate-400">
                      <div className="text-center"><MessageCircle className="h-8 w-8 mx-auto mb-2 opacity-50" /><span className="text-xs font-medium">Click to add SMS</span></div>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              localSteps.map((step, index) => {
                const isLoadingStep = (step as any).loading;
                const hasContent = hasSmsMessageContent(step.messageBody);
                const lacksOptOut = hasContent && !hasSmsOptOut(step.messageBody);
                const mediaCount = step.mediaUrls?.length || 0;
                const segments = getSmsSegments(step.messageBody || '');
                const canReorder = localSteps.length > 1 && !isLoadingStep && !reorderMut.isPending;
                const isDragged = stepDrag.dragIndex === index;
                return (
                  <div
                    key={step.stepId}
                    data-step-card
                    onDragEnter={stepDrag.handleDragEnter}
                    onDragLeave={stepDrag.handleDragLeave}
                    onDragOver={(e) => stepDrag.handleDragOver(e, index)}
                    onDrop={stepDrag.handleDrop}
                    className={cn('relative', isDragged && 'opacity-40')}
                  >
                    {stepDrag.dropIndex === index && (
                      <div className="absolute -top-1 left-0 right-0 h-0.5 bg-blue-500 rounded-full z-10 pointer-events-none" />
                    )}
                    <div
                      className={cn('flex items-center gap-3 p-3 bg-white border border-slate-200 rounded transition-colors', !isLoadingStep && 'cursor-pointer hover:border-blue-500')}
                      onClick={() => !isLoadingStep && openTimingModal(index)}
                    >
                      <span className="flex-1 text-sm text-slate-700">
                        {isLoadingStep ? <><Spinner size="sm" className="inline mr-2" />Loading...</> : formatDelay(step.delayMinutes, index)}
                      </span>
                      {!isLoadingStep && <Pencil className="h-4 w-4 text-slate-400" />}
                    </div>
                    <div className="w-0.5 h-6 bg-slate-200 ml-5" />
                    <div className={cn('bg-white border border-slate-200 rounded overflow-hidden', isLoadingStep && 'opacity-70 pointer-events-none')}>
                      <div className="flex items-stretch">
                        {/* The step label doubles as the drag handle — see the
                            email editor for why draggable sits here only. */}
                        <div
                          className={cn(
                            'bg-slate-200 px-1.5 py-3 text-[11px] font-semibold text-slate-500 flex flex-col items-center justify-center gap-1.5 select-none',
                            canReorder && 'cursor-grab active:cursor-grabbing hover:bg-slate-300 transition-colors',
                          )}
                          draggable={canReorder}
                          onDragStart={(e) => stepDrag.handleDragStart(
                            e,
                            index,
                            (e.currentTarget as HTMLElement).closest('[data-step-card]') as HTMLElement | null,
                          )}
                          onDragEnd={stepDrag.handleDragEnd}
                          title={canReorder ? 'Drag to reorder this message' : undefined}
                        >
                          {canReorder && <GripVertical className="h-3.5 w-3.5 shrink-0" />}
                          <span style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>SMS {index + 1}</span>
                        </div>
                        {/* Preview */}
                        <div
                          className="flex-[0_1_50%] max-w-[50%] min-h-[160px] bg-slate-50 border-r border-slate-200 overflow-hidden relative cursor-pointer p-3.5 group"
                          onClick={() => !isLoadingStep && openStepEditor(index)}
                        >
                          {isLoadingStep ? (
                            <div className="flex items-center justify-center h-full"><Spinner size="sm" /></div>
                          ) : hasContent || mediaCount > 0 ? (
                            <>
                              {hasContent && <div className="text-xs text-slate-700 line-clamp-6 whitespace-pre-wrap">{step.messageBody}</div>}
                              {mediaCount > 0 && (
                                <div className="flex flex-wrap gap-1.5 mt-2">
                                  {step.mediaUrls!.map((url, mi) => (
                                    /\.(jpe?g|png|gif|webp)(\?|$)/i.test(url)
                                      ? <img key={mi} src={url} alt="" className="w-12 h-12 rounded object-cover border border-slate-200" />
                                      : <div key={mi} className="w-12 h-12 rounded border border-slate-200 bg-slate-100 flex items-center justify-center"><FileText className="h-4 w-4 text-slate-400" /></div>
                                  ))}
                                </div>
                              )}
                              <div className="absolute inset-0 bg-blue-600/80 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                                <span className="text-white text-xs font-semibold">Edit Content</span>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="flex flex-col items-center justify-center h-full min-h-[100px] text-slate-400 text-center">
                                <MessageCircle className="h-8 w-8 mb-2 opacity-50" />
                                <span className="text-xs font-medium">Add SMS Content</span>
                              </div>
                              <div className="absolute inset-0 bg-blue-600/80 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                                <span className="text-white text-xs font-semibold">Create SMS</span>
                              </div>
                            </>
                          )}
                        </div>
                        {/* Details */}
                        <div className="flex-1 min-w-[140px] p-4 flex flex-col justify-center text-sm space-y-2">
                          <div><strong className="text-slate-700">Message</strong> {isLoadingStep ? <span className="text-red-500 italic">Loading...</span> : hasContent ? <span className="text-blue-600">{(step.messageBody?.length || 0)} characters{lacksOptOut && <span className="text-red-500 italic"> · no opt-out instructions</span>}</span> : <span className="text-red-500 italic">Add message content</span>}</div>
                          <div><strong className="text-slate-700">Segments</strong> {isLoadingStep ? <span className="text-slate-400">Loading...</span> : <span className={getSegmentColor(segments)}>{segments} {segments === 1 ? 'segment' : 'segments'}</span>}</div>
                          <div><strong className="text-slate-700">Media</strong> {isLoadingStep ? <span className="text-slate-400">Loading...</span> : mediaCount > 0 ? <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 rounded text-xs font-semibold text-slate-600"><Paperclip className="h-3 w-3" /> {mediaCount} {mediaCount === 1 ? 'file' : 'files'}</span> : <span className="text-slate-400">None</span>}</div>
                        </div>
                        {/* Menu */}
                        {!isLoadingStep && (
                          <div className="p-4">
                            <AutomationStepMenu
                              stepNumber={index + 1}
                              canMoveUp={index > 0 && canReorder}
                              canMoveDown={index < localSteps.length - 1 && canReorder}
                              onEdit={() => openStepEditor(index)}
                              onMoveUp={() => moveStep(index, -1)}
                              onMoveDown={() => moveStep(index, 1)}
                              onDelete={() => handleDeleteStep(index)}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                    {index < localSteps.length - 1 && <div className="w-0.5 h-6 bg-slate-200 ml-5" />}
                    {stepDrag.dropIndex === index + 1 && index === localSteps.length - 1 && (
                      <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-blue-500 rounded-full z-10 pointer-events-none" />
                    )}
                  </div>
                );
              })
            )}
            <button
              type="button"
              className="mt-4 flex items-center justify-center gap-2 w-full py-3 px-6 bg-white border border-blue-500 rounded text-blue-600 text-sm font-medium hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={localSteps.some(s => (s as any).loading)}
              onClick={handleAddStep}
            >
              <Plus className="h-4 w-4" /> Add an SMS
            </button>
          </div>
        </div>
      </div>

      {/* ── Step Editor Modal (Full-screen, portaled to body to escape stacking context) ── */}
      {showStepEditor && createPortal(
        <div className="fixed inset-0 bg-slate-100 z-[9999] flex flex-col">
          <div className="flex items-center justify-between px-5 py-3 bg-slate-900 text-white">
            <div className="flex items-center gap-3">
              <span className="text-lg font-semibold">Edit SMS Content</span>
              <span className="bg-blue-600 text-white text-xs font-medium px-2.5 py-1 rounded">SMS {editingStepIndex + 1}</span>
            </div>
            <div className="flex items-center gap-3">
              {stepEditorUnsaved && (
                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-amber-900/30 border border-amber-500 rounded text-amber-300 text-xs font-medium">
                  <span className="w-2 h-2 bg-amber-400 rounded-full animate-pulse" />Unsaved
                </div>
              )}
              <Button variant="ghost" size="sm" className="text-white bg-blue-600/50 border border-blue-400" onClick={() => setShowTemplatePicker(true)}>
                <FileText className="h-4 w-4 mr-1" /> Use Template
              </Button>
              <Button variant="ghost" size="sm" className="text-white border-slate-600" onClick={handleCloseStepEditor}>
                <X className="h-4 w-4 mr-1" /> Done
              </Button>
              <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={handleSaveStepContent}>
                <Save className="h-4 w-4 mr-1" /> Save Content
              </Button>
            </div>
          </div>
          <div className="flex-1 flex min-h-0 overflow-hidden">
            <div className="flex-[3] overflow-y-auto p-5">
              <div className="mb-4">
                <label className="block text-base font-bold text-slate-800 mb-2">Message Content</label>
              </div>
              <SmsEditor
                messageBody={editorMessageBody}
                onMessageChange={(val) => { setEditorMessageBody(val); if (!suppressUnsaved) setStepEditorUnsaved(true); }}
                mediaUrls={editorMediaUrls}
                onMediaChange={(urls) => { setEditorMediaUrls(urls); if (!suppressUnsaved) setStepEditorUnsaved(true); }}
                photoPickerZClassName="z-[10001]"
              />
            </div>
            <div className="flex-[2] flex items-start justify-center pt-5 overflow-y-auto bg-slate-200/50">
              <SmsPhoneMockup messageBody={editorMessageBody} mediaUrls={editorMediaUrls} appendOptOut />
            </div>
          </div>
        </div>,
        document.body,
      )}

      {/* ── SMS Template Picker Modal ── */}
      <SmsTemplatePickerModal
        isOpen={showTemplatePicker}
        onClose={() => setShowTemplatePicker(false)}
        onApply={handleApplyTemplate}
        zClassName="z-[10000]"
      />

      {/* ── Timing Modal ── */}
      <Modal isOpen={showTimingModal} onClose={() => setShowTimingModal(false)} title="Edit Timing" size="md">
        <div className="space-y-4">
          <FormField label="Wait time before sending" helpText="Set to 0 to send immediately">
            <div className="inline-flex items-center gap-1.5">
              <Input type="number" min={0} value={timingValue} onChange={e => setTimingValue(Number(e.target.value))} className="w-16" />
              <Select value={timingUnit} onChange={v => setTimingUnit(v || 'days')} options={DELAY_UNIT_OPTIONS} className="w-24" />
              <span className="text-sm text-slate-500 whitespace-nowrap">after previous step</span>
            </div>
          </FormField>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setShowTimingModal(false)}>Cancel</Button>
            <Button onClick={handleSaveTiming}>Save</Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirm */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title="Delete Automation"
        message="Are you sure you want to delete this automation? This action cannot be undone."
        confirmLabel="Delete"
        variant="danger"
        onConfirm={() => deleteMut.mutate()}
        onClose={() => setShowDeleteConfirm(false)}
      />

      {/* Pause Confirm */}
      <PauseAutomationDialog
        isOpen={pauseConfirmOpen}
        channel="sms"
        automationName={automationName || automation?.name}
        activeContacts={contacts.active}
        onClose={() => setPauseConfirmOpen(false)}
        onConfirm={confirmPause}
      />
    </div>
  );
}
